import { DatePipe } from '@angular/common';
import { DecimalPipe } from '@angular/common';
import { TranslatePipe } from '../../filters/translate.pipe';
import { CapitalizePipe } from '../../filters/capitalize.pipe';
import { ItemProgressComponent } from '../../components/item-progress/item-progress.component';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { ItemRatingComponent } from '../../components/item-rating/item-rating.component';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import Config from '../../utils/config.service';
import RestService from '../../services/core-rest.service';
import Session from '../../utils/session.service';
import DialogServices from '../../services/dialog.service';
import AngularBroadcastHelperService from '../../services/angular-broadcast-helper.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ModalConditionInfoComponent } from '../modal-condition-info/modal-condition-info.component';
import { ModalSymptomCheckFeedbackComponent } from '../modal-symptom-check-feedback/modal-symptom-check-feedback.component';
import { ModalTreatmentGuidelineComponent } from '../modal-treatment-guideline/modal-treatment-guideline.component';
import { Subscription } from 'rxjs';

@Component({
	standalone: true,
	selector: 'modal-symptom-check-info',
	templateUrl: './modal-symptom-check-info.component.html',
	imports: [
		CommonModule, DatePipe, DecimalPipe, TranslatePipe, 
		CapitalizePipe, ItemProgressComponent, LoaderOverlayComponent, 
		AvatarComponent, ItemRatingComponent, FormsModule
	]
})
export class ModalSymptomCheckInfoComponent implements OnInit, OnDestroy {
	private config: Config = inject(Config);
	private restService: RestService = inject(RestService);
	private session: Session = inject(Session);
	private dialogServices: DialogServices = inject(DialogServices);
	private angularBroadcastHelperService: AngularBroadcastHelperService = inject(AngularBroadcastHelperService);
	private dialogRef = inject(MatDialogRef<ModalSymptomCheckInfoComponent>);
	private data = inject(MAT_DIALOG_DATA);
	
	vm: any = undefined;
	broadcastSubscriptions: Array<Subscription> = [];
	
	constructor() {
		this.vm = this;
	}
	
	ngOnInit() {
		Object.keys(this.data).forEach(key => this.vm[key] = this.data[key]);
		
		this.vm.user = this.session.getSession().user;
		this.vm.ref = Date.now();
		this.vm.isDoctor = this.vm.user.businessUserType === 'SERVICE_PROVIDER';
		
		var promise;
		if (this.vm.preVisitTicketId) {
			promise = this.restService.getPreVisitInterview({ ticketId: this.vm.preVisitTicketId });
		} else {
			promise = this.restService.getInterview({ interviewId: this.vm.interviewId });
		}
		promise
			.then((res) => {
				this.vm.interview = res;
				if (this.vm.interview.diagnosisHistory && this.vm.interview.diagnosisHistory.length) {
					var history: any = this.vm.interview.diagnosisHistory;
					var conditions: any = history.pop();
					this.vm.conditions = conditions.diagnosisConditions;
					this.vm.questions = history.map(((item) => { return item.question; }));
				}
				this.vm.genericFactors = this.vm.interview.evidence.filter((evidence) => {
					return evidence.riskTag === 'GENERIC' && evidence.choice_id === 'present';
				});
				this.vm.locationFactors = this.vm.interview.evidence.filter((evidence) => {
					return evidence.riskTag === 'LOCATION' && evidence.choice_id === 'present';
				});
				this.vm.initialFactors = this.vm.interview.evidence.filter((evidence) => {
					return evidence.initial || evidence.source == 'initial';
				});

				this.broadcastSubscriptions.push(
					this.angularBroadcastHelperService.onEvent(this,
						this.config.getBroadcastConfig().Symptom_Check.RATING_UPDATED + this.vm.ref,
						(e, data) => { this.handleRatingUpdate(e, data); }
					)
				);

			}).then(() => {
				var conditionRemoteIds: Array<any> = [];
				for (var conditionsKey in this.vm.conditions) {
					conditionRemoteIds.push(this.vm.conditions[conditionsKey].condition.id);
				}
				this.restService.getConditionTreatmentGuidelines({ conditionRemoteId: conditionRemoteIds })
					.then((response) => {
						this.vm.conditionTreatmentGuidelines = response;

						this.vm.isInitialized = true;
					}).catch((error) => {
						console.log(error);
						this.vm.isInitialized = true;
					});
			})
			.catch((err) => {
				console.error(err);
			});
		
		this.vm.close = this.close;
		this.vm.getSingleItem = this.getSingleItem;
		this.vm.getSingleGroupItem = this.getSingleGroupItem;
		this.vm.openConditionInfo = this.openConditionInfo;
		this.vm.openSymptomRatingModal = this.openSymptomRatingModal;
		this.vm.hasTreatmentGuideline = this.hasTreatmentGuideline;
		this.vm.openTreatmentGuideline = this.openTreatmentGuideline;
	}
	
	ngOnDestroy() {
		for (var b = 0; b < this.broadcastSubscriptions.length; b++) {
			this.broadcastSubscriptions[b].unsubscribe();
		}
	}
	
	close() {
		this.dialogRef.close();
	}
	
	handleRatingUpdate(e, data) {
		if (!data || !data.rating) return;
		this.vm.interview.rating = data.rating;
	}
	
	getSingleItem(item) {
		return item.choices.filter((choice) => {
			return this.vm.interview.evidence.filter((evidence) => {
				return evidence.id === item.id && evidence.choice_id === choice.id;
			}).length;
		})[0];
	}
	
	getSingleGroupItem(question) {
		return question.items.filter((item) => {
			return this.vm.interview.evidence.filter((evidence) => {
				return evidence.id === item.id;
			}).length;
		})[0];
	}
	
	openConditionInfo(condition) {
		if (!condition) return;
		this.dialogServices.show({
			controller: ModalConditionInfoComponent,
			condition: condition,
			interview: this.vm.interview
		});
	}
	
	openSymptomRatingModal() {
		if (!this.vm.isDoctor) return;
		this.dialogServices.show({
			controller: ModalSymptomCheckFeedbackComponent,
			interview: this.vm.interview,
			ref: this.vm.ref
		});
	}
	
	hasTreatmentGuideline(conditionId) {
		for (var index in this.vm.conditionTreatmentGuidelines) {
			if (this.vm.conditionTreatmentGuidelines[index].conditionRemoteId === conditionId) {
				return true;
			}
		}
		return false;
	}
	
	openTreatmentGuideline(conditionId) {
		var guideline: any = undefined;
		for (var index in this.vm.conditionTreatmentGuidelines) {
			if (this.vm.conditionTreatmentGuidelines[index].conditionRemoteId === conditionId) {
				guideline = this.vm.conditionTreatmentGuidelines[index];
			}
		}
		if (!guideline) {
			return;
		}
		var options: any = {
			title: guideline.conditionName,
			text: guideline.guidelines
		}
		this.dialogServices.show({
			controller: ModalTreatmentGuidelineComponent,
			options: options
		});
	}
	
}